// CPF Validation and Formatting Utilities

/**
 * Formats CPF string to XXX.XXX.XXX-XX format
 */
export const formatCPF = (value: string): string => {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, '');
  
  // Limit to 11 digits
  const limited = digitsOnly.slice(0, 11);
  
  // Apply formatting
  if (limited.length <= 3) {
    return limited;
  } else if (limited.length <= 6) {
    return `${limited.slice(0, 3)}.${limited.slice(3)}`;
  } else if (limited.length <= 9) {
    return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6)}`;
  } else {
    return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6, 9)}-${limited.slice(9)}`;
  }
};

/**
 * Removes formatting from CPF string, returning only digits
 */
export const unformatCPF = (value: string): string => {
  return value.replace(/\D/g, '');
};

/**
 * Validates CPF format and checksum
 */
export const validateCPF = (cpf: string): boolean => {
  const digitsOnly = unformatCPF(cpf);
  
  // Check if has 11 digits
  if (digitsOnly.length !== 11) {
    return false;
  }
  
  // Check if all digits are the same (invalid CPF)
  if (/^(\d)\1{10}$/.test(digitsOnly)) {
    return false;
  }
  
  // Validate check digits
  let sum = 0;
  let remainder: number;
  
  // Validate first check digit
  for (let i = 1; i <= 9; i++) {
    sum += parseInt(digitsOnly.substring(i - 1, i)) * (11 - i);
  }
  
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) {
    remainder = 0;
  }
  
  if (remainder !== parseInt(digitsOnly.substring(9, 10))) {
    return false;
  }
  
  sum = 0;
  
  // Validate second check digit
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(digitsOnly.substring(i - 1, i)) * (12 - i);
  }
  
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) {
    remainder = 0;
  }
  
  if (remainder !== parseInt(digitsOnly.substring(10, 11))) {
    return false;
  }
  
  return true;
};

/**
 * Gets CPF validation error message
 */
export const getCPFError = (cpf: string, showAllErrors: boolean = false): string | null => {
  if (!cpf || cpf.trim() === '') {
    return null; // Don't show error for empty field
  }
  
  const digitsOnly = unformatCPF(cpf);
  
  // Only show "incomplete" message if user has started typing
  if (digitsOnly.length > 0 && digitsOnly.length < 11) {
    return showAllErrors ? 'CPF deve ter 11 dígitos' : null;
  }
  
  // Only validate if we have 11 digits
  if (digitsOnly.length === 11 && !validateCPF(cpf)) {
    return 'CPF inválido';
  }
  
  return null;
};

